import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DeleteSuscriptionPost } from "@/services/types";

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { logout } from "@/firebase";
import { useApiV2SubscriptionReasonsUserRetrieve } from "@/services/api/cancelación-flujo-de-usuario/cancelación-flujo-de-usuario";
import { deleteMutation } from "@/services/queries";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useReasonsToLeaveQuery } from "./api";
import { Advice } from "./components/Advice";
import { OfertaEspecial } from "./components/OfertaEspecial";
import type { Reason, ReasonCategory } from "./types";
import {
    CancelSubscriptionFormSchema,
    type CancelSubscriptionFormValues,
} from "./types";

export type DeleteAccountProps = {
    open: boolean;
    onClose: () => void;
    onPausar: () => void;
    onChange: () => void;
};
const DeleteAccount = ({
    open,
    onClose,
    onPausar,
    onChange,
}: DeleteAccountProps) => {
    const navigate = useNavigate();

    const form = useForm<CancelSubscriptionFormValues>({
        resolver: zodResolver(CancelSubscriptionFormSchema),
        defaultValues: {
            user_action: "TERMINATE",
            termination_reason: "",
            selected_tags: [],
            termination_comments: "",
        },
        mode: "onChange", // Validar en cada cambio para la lógica condicional
    });
    const { data, isLoading } = useApiV2SubscriptionReasonsUserRetrieve();

    const deleteSuscriptionMutation = deleteMutation();
    const [showGiftOffer, setShowGiftOffer] = useState(false);
    const [category, setCategory] = useState<ReasonCategory>();
    const [reason, setReason] = useState<Reason>();
    const [userFrom, setUserForm] = useState<DeleteSuscriptionPost>();
    const selectedTags = form.watch("selected_tags") || []; // Observar tags del formulario

    const handleTagClick = (tag: string) => {
        const currentTags = form.getValues("selected_tags") || [];
        const newTags = currentTags.includes(tag)
            ? currentTags.filter((t) => t !== tag)
            : [...currentTags, tag];
        form.setValue("selected_tags", newTags, { shouldValidate: true }); // Actualizar y revalidar
    };

    const handleDeleteSubscription = (data: DeleteSuscriptionPost) => {
        if (data) {
            deleteSuscriptionMutation.mutate(data);
        }
    };

    const handleSubmit = (values: CancelSubscriptionFormValues) => {
        // Si hay tags seleccionados, agregarlos a los comentarios
        let finalComments = values.termination_comments || "";
        if (values.selected_tags && values.selected_tags.length > 0) {
            const tagsText = values.selected_tags.join(", ");
            finalComments = finalComments
                ? `${finalComments}\n\nTags seleccionados: ${tagsText}`
                : `Tags seleccionados: ${tagsText}`;
        }
        const submissionData = {
            user_action: "TERMINATE" as const,
            termination_reason: `${values.termination_main_reason}\n\n ${finalComments}`,
        };
        setUserForm(submissionData);
        if (reason?.regalo && showGiftOffer === false) {
            setShowGiftOffer(true);
        } else {
            handleDeleteSubscription(submissionData);
        }
    };

    const validToGift = data?.gift && userFrom && !isLoading;

    const reasonsQuery = useReasonsToLeaveQuery();

    // Redirigir al éxito de la mutación
    useEffect(() => {
        if (deleteSuscriptionMutation.isSuccess) {
            logout().finally(() => {
                navigate({
                    to: "/",
                });
            });
        }
    }, [deleteSuscriptionMutation.isSuccess, navigate]);

    // Efecto que corre al cambiar category para resetear sub-opciones y tags
    const categoryValue = form.watch("termination_main_reason");
    useEffect(() => {
        if (reasonsQuery.data) {
            const selectedCategory = reasonsQuery.data.reasons.find(
                (r) => r.base === categoryValue,
            );
            setCategory(selectedCategory);
            setReason(undefined);
            form.setValue("selected_tags", []); // Resetear tags seleccionados
            form.setValue("termination_reason", ""); // Resetear razón específica
            form.setValue("termination_comments", ""); // Resetear comentarios
        }
    }, [categoryValue, form, reasonsQuery.data]);

    // Efecto para actualizar consejos, regalos y tags disponibles basados en la reason seleccionada
    const reasonValue = form.watch("termination_reason");
    useEffect(() => {
        if (reasonValue) {
            const selecteReason = category?.reasones.find(
                (sr) => sr.razon.title === reasonValue,
            );
            setReason(selecteReason);
        } else {
            form.setValue("selected_tags", []);
        }
        // console.log(reason);
        form.setValue("selected_tags", []); // Resetear tags al cambiar la reason
    }, [reasonValue, form, category]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="overflow-x-visible">
                <Form {...form}>
                    <form
                        className="space-y-4"
                        onSubmit={form.handleSubmit(handleSubmit)}
                    >
                        <DialogHeader>
                            <DialogTitle>
                                Lamentamos no cumplir tus expectativas
                            </DialogTitle>
                            <DialogDescription>
                                Al confirmar, tu suscripción quedara desactivada
                                y no haremos ningún cobro adicional.
                            </DialogDescription>
                        </DialogHeader>
                        {/* inicio del scroll */}
                        <div className="space-y-4 px-1 overflow-x-visible overflow-y-scroll h-[70vh]">
                            <div className="w-10 mx-auto">
                                <img
                                    src="/sad_box.png"
                                    alt="Caja triste"
                                    className="w-full"
                                />
                            </div>

                            {reasonsQuery.isLoading && (
                                <div className="text-center py-4">
                                    <p>Cargando opciones...</p>
                                </div>
                            )}

                            {reasonsQuery.isError && (
                                <div className="text-center py-4 text-red-500">
                                    <p>
                                        Error al cargar las opciones. Por favor,
                                        intenta de nuevo.
                                    </p>
                                </div>
                            )}
                            <FormField
                                control={form.control}
                                name="termination_main_reason"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel htmlFor="termination_main_reason">
                                            ¿Qué motivó tu cancelación?
                                        </FormLabel>
                                        <Select
                                            value={field.value}
                                            onValueChange={(value) =>
                                                field.onChange(value)
                                            }
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Selecciona..." />
                                                </SelectTrigger>
                                            </FormControl>

                                            <SelectContent>
                                                {reasonsQuery.data?.reasons.map(
                                                    (reason) => (
                                                        <SelectItem
                                                            key={reason.base}
                                                            value={reason.base}
                                                        >
                                                            {reason.base}
                                                        </SelectItem>
                                                    ),
                                                ) || []}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {category?.reasones && (
                                <FormField
                                    control={form.control}
                                    name="termination_reason"
                                    render={({ field }) => {
                                        return (
                                            <>
                                                <FormItem>
                                                    <FormLabel htmlFor="termination_reason">
                                                        ¿Podrías darnos más
                                                        detalles?
                                                    </FormLabel>
                                                    <Select
                                                        onValueChange={
                                                            field.onChange
                                                        }
                                                        value={
                                                            field.value || ""
                                                        } // Asegura que el valor sea una cadena
                                                    >
                                                        <FormControl>
                                                            <SelectTrigger>
                                                                <SelectValue placeholder="" />
                                                            </SelectTrigger>
                                                        </FormControl>
                                                        <SelectContent>
                                                            {category.reasones.map(
                                                                (r) => (
                                                                    <SelectItem
                                                                        key={
                                                                            r
                                                                                .razon
                                                                                .title
                                                                        }
                                                                        value={
                                                                            r
                                                                                .razon
                                                                                .title
                                                                        }
                                                                    >
                                                                        {
                                                                            r
                                                                                .razon
                                                                                .title
                                                                        }
                                                                    </SelectItem>
                                                                ),
                                                            )}
                                                        </SelectContent>
                                                    </Select>
                                                    <FormMessage />
                                                </FormItem>
                                                {reason?.razon.body && (
                                                    <Advice
                                                        card={reason.razon}
                                                        onChange={onChange}
                                                        onPausar={onPausar}
                                                    />
                                                )}

                                                {reason?.tags && (
                                                    <div className="my-4">
                                                        <div className="flex flex-wrap gap-2 mt-2">
                                                            {reason.tags.map(
                                                                (tag) => (
                                                                    <Badge
                                                                        key={
                                                                            tag
                                                                        }
                                                                        variant={
                                                                            selectedTags.includes(
                                                                                tag,
                                                                            )
                                                                                ? "default"
                                                                                : "outline"
                                                                        }
                                                                        className="cursor-pointer hover:bg-primary/10 transition-colors select-none"
                                                                        onClick={(
                                                                            e,
                                                                        ) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleTagClick(
                                                                                tag,
                                                                            );
                                                                        }}
                                                                    >
                                                                        {tag}
                                                                    </Badge>
                                                                ),
                                                            )}
                                                        </div>
                                                        {selectedTags.length >
                                                            0 && (
                                                            <div className="mt-2 text-xs text-muted-foreground">
                                                                Seleccionados:{" "}
                                                                {selectedTags.join(
                                                                    ", ",
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </>
                                        );
                                    }}
                                />
                            )}
                            <FormField
                                control={form.control}
                                name="termination_comments"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Déjanos más información
                                        </FormLabel>
                                        {/* <FormDescription>
                        Valoramos mucho tus comentarios.
                      </FormDescription> */}

                                        <FormControl>
                                            <Textarea
                                                onChange={field.onChange}
                                                value={field.value || ""} // Asegura que el valor sea una cadena
                                                className="text-base md:text-sm"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* fin scroll  */}

                        <DialogFooter className="my-4 flex flex-col lg:justify-end lg:flex-row gap-4">
                            <DialogClose asChild>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onClose}
                                    disabled={
                                        deleteSuscriptionMutation.isPending
                                    }
                                >
                                    Cerrar
                                </Button>
                            </DialogClose>
                            <Button
                                type="submit"
                                variant="destructive"
                                disabled={
                                    deleteSuscriptionMutation.isPending ||
                                    !form.formState.isValid ||
                                    reasonsQuery.isLoading
                                }
                            >
                                {deleteSuscriptionMutation.isPending
                                    ? "Guardando..."
                                    : "Desactivar suscripción"}
                            </Button>
                        </DialogFooter>

                        {deleteSuscriptionMutation.isError && (
                            <FormMessage className="text-right">
                                {deleteSuscriptionMutation.error.message}
                            </FormMessage>
                        )}
                        {deleteSuscriptionMutation.isSuccess && (
                            <FormMessage className="text-right text-primary">
                                Tu suscripción se ha desactivado correctamente.
                            </FormMessage>
                        )}
                    </form>
                </Form>
            </DialogContent>

            {/* Oferta Especial Dialog */}
            {validToGift && reason?.regalo && (
                <OfertaEspecial
                    open={showGiftOffer}
                    onClose={() => setShowGiftOffer(false)}
                    gift={reason?.regalo}
                    deleteSuscriptionData={userFrom}
                    onConfirm={() => handleDeleteSubscription(userFrom)}
                    isLoading={deleteSuscriptionMutation.isPending}
                />
            )}
        </Dialog>
    );
};

export default DeleteAccount;
